import { supabase } from '../config/supabase';

export interface WaitlistEntry {
  email: string;
  source?: string;
  referrer?: string;
  metadata?: Record<string, any>;
}

export interface WaitlistResponse {
  id: string;
  email: string;
  created_at: string;
  source: string;
  referrer: string | null;
  metadata: Record<string, any>;
}

class WaitlistService {
  /**
   * Add an email to the waitlist
   */
  async addToWaitlist(entry: WaitlistEntry): Promise<{ success: boolean; error?: string }> {
    try {
      // Get referrer from document if not provided
      const referrer = entry.referrer || document.referrer || null;

      // Collect metadata
      const metadata = {
        ...entry.metadata,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
      };

      const { error } = await supabase
        .from('waitlist')
        .insert({
          email: entry.email.toLowerCase().trim(),
          source: entry.source || 'coming_soon_page',
          referrer,
          metadata,
        })
        .select()
        .single();

      if (error) {
        // Check if it's a duplicate email error
        if (error.code === '23505') {
          return { success: false, error: 'This email is already on the waitlist!' };
        }
        console.error('Error adding to waitlist:', error);
        return { success: false, error: 'Failed to join waitlist. Please try again.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Unexpected error in addToWaitlist:', error);
      return { success: false, error: 'An unexpected error occurred. Please try again.' };
    }
  }

  /**
   * Check if an email is already on the waitlist
   */
  async isEmailOnWaitlist(email: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('id')
        .eq('email', email.toLowerCase().trim())
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 means no rows found, which is fine
        console.error('Error checking waitlist:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Unexpected error in isEmailOnWaitlist:', error);
      return false;
    }
  }

  /**
   * Get waitlist statistics (for admin use)
   */
  async getWaitlistStats(): Promise<{
    total: number;
    todaySignups: number;
    sources: Record<string, number>;
  } | null> {
    try {
      // Get total count
      const { count: total } = await supabase
        .from('waitlist')
        .select('*', { count: 'exact', head: true });

      // Get today's signups
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count: todaySignups } = await supabase
        .from('waitlist')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      // Get source breakdown
      const { data: sourceData } = (await supabase.from('waitlist').select('source')) as {
        data: { source: string }[] | null;
      };

      const sources: Record<string, number> = {};
      if (sourceData) {
        sourceData.forEach((entry) => {
          sources[entry.source] = (sources[entry.source] || 0) + 1;
        });
      }

      return {
        total: total || 0,
        todaySignups: todaySignups || 0,
        sources,
      };
    } catch (error) {
      console.error('Error getting waitlist stats:', error);
      return null;
    }
  }
}

export const waitlistService = new WaitlistService();
